import React, { useState, useEffect } from "react";

const API_BASE_LOC = "https://registration.labourpartynigeria.org.ng:8443/api/locations";

const LocationFilter = ({ filters, setFilters, adminRole }) => {
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [wards, setWards] = useState([]);
  const [units, setUnits] = useState([]);

  // Loading states for each level
  const [loading, setLoading] = useState({
    states: false,
    lgas: false,
    wards: false,
    units: false
  });

  // 1. Fetch States on Mount
  useEffect(() => {
    const fetchStates = async () => {
      setLoading(prev => ({ ...prev, states: true }));
      try {
        const res = await fetch(`${API_BASE_LOC}/states`);
        const data = await res.json();
        setStates(data);
      } catch (e) { 
        console.error("Error fetching states", e); 
      } finally {
        setLoading(prev => ({ ...prev, states: false }));
      }
    };
    fetchStates();
  }, []);

  // 2. Fetch LGAs when State changes
  useEffect(() => {
    if (!filters.state) {
      setLgas([]);
      return;
    }
    const fetchLgas = async () => {
      setLoading(prev => ({ ...prev, lgas: true }));
      try {
        const res = await fetch(`${API_BASE_LOC}/lgas?state=${encodeURIComponent(filters.state)}`);
        const data = await res.json();
        setLgas(data);
      } catch (e) {
        console.error("Error fetching LGAs", e);
      } finally {
        setLoading(prev => ({ ...prev, lgas: false }));
      }
    };
    fetchLgas();
  }, [filters.state]);

  // 3. Fetch Wards when LGA changes
  useEffect(() => {
    if (!filters.lga) {
      setWards([]);
      return;
    }
    const fetchWards = async () => {
      setLoading(prev => ({ ...prev, wards: true }));
      try {
        const res = await fetch(`${API_BASE_LOC}/wards?state=${encodeURIComponent(filters.state)}&lga=${encodeURIComponent(filters.lga)}`);
        const data = await res.json();
        setWards(data);
      } catch (e) {
        console.error("Error fetching Wards", e);
      } finally {
        setLoading(prev => ({ ...prev, wards: false }));
      }
    };
    fetchWards();
  }, [filters.lga, filters.state]);

  // 4. Fetch Polling Units when Ward changes
  useEffect(() => {
    if (!filters.ward) {
      setUnits([]);
      return;
    }
    const fetchUnits = async () => {
      setLoading(prev => ({ ...prev, units: true }));
      try {
        const res = await fetch(`${API_BASE_LOC}/polling-units?state=${encodeURIComponent(filters.state)}&lga=${encodeURIComponent(filters.lga)}&ward=${encodeURIComponent(filters.ward)}`);
        const data = await res.json();
        setUnits(data);
      } catch (e) {
        console.error("Error fetching PUs", e);
      } finally {
        setLoading(prev => ({ ...prev, units: false }));
      }
    };
    fetchUnits();
  }, [filters.ward, filters.lga, filters.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Cascading reset: If parent changes, children must be cleared
    const updates = { [name]: value };
    if (name === "state") { updates.lga = ""; updates.ward = ""; updates.pollingUnit = ""; }
    if (name === "lga") { updates.ward = ""; updates.pollingUnit = ""; }
    if (name === "ward") { updates.pollingUnit = ""; }
    
    setFilters(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="location-filter-group">
      {/* STATE SELECT */}
      {adminRole === "National" && (
        <div className="mb-2">
          <label className="small fw-bold text-muted">State {loading.states && "..."}</label>
          <select 
            name="state" 
            className="form-select form-select-sm" 
            value={filters.state} 
            onChange={handleChange}
            disabled={loading.states}
          >
            <option value="">{loading.states ? "Loading States..." : "All States"}</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {/* LGA SELECT */}
      {(adminRole === "National" || adminRole === "State") && (
        <div className="mb-2">
          <label className="small fw-bold text-muted">LGA {loading.lgas && "..."}</label>
          <select 
            name="lga" 
            className="form-select form-select-sm" 
            value={filters.lga} 
            onChange={handleChange} 
            disabled={!filters.state || loading.lgas}
          >
            <option value="">{loading.lgas ? "Loading LGAs..." : "All LGAs"}</option>
            {lgas.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      )}

      {/* WARD & POLLING UNIT ROW */}
      <div className="row g-2 mb-2">
        <div className="col-6">
          <label className="small fw-bold text-muted">Ward {loading.wards && "..."}</label>
          <select 
            name="ward" 
            className="form-select form-select-sm" 
            value={filters.ward} 
            onChange={handleChange} 
            disabled={!filters.lga || loading.wards}
          >
            <option value="">{loading.wards ? "..." : "All Wards"}</option>
            {wards.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div className="col-6">
          <label className="small fw-bold text-muted">P. Unit {loading.units && "..."}</label>
          <select 
            name="pollingUnit" 
            className="form-select form-select-sm" 
            value={filters.pollingUnit} 
            onChange={handleChange} 
            disabled={!filters.ward || loading.units}
          >
            <option value="">{loading.units ? "..." : "All PUs"}</option>
            {units.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
};

export default LocationFilter;